import type { DateRange } from "../types/trade";
import {
  lastMonthLocalRange,
  lastWeekMonSunLocalRange,
  localDateRangeMatchesLastMonth,
  localDateRangeMatchesLastWeek,
  localDateRangeMatchesThisMonth,
  localDateRangeMatchesThisWeek,
  thisMonthStartThroughTodayLocalRange,
  thisWeekMonTodayLocalRange,
} from "./calendarWeekRanges";

export const DASHBOARD_DATE_WINDOW_KEY = "dashboard_date_window_v1";

/** Dispatched on same-tab updates after write; cross-tab uses `storage`. */
export const DASHBOARD_DATE_WINDOW_EVENT = "dashboardDateWindowChanged";

export type DashboardDateWindowV1 = {
  v: 1;
  mode: "preset" | "custom";
  timeframe: string;
  startDate: string | null;
  endDate: string | null;
  /**
   * When `mode === "custom"`, set on write. Custom windows are dropped after the local
   * calendar day changes so fixed dates (e.g. March 27) do not stick across sessions.
   */
  savedAtMs?: number;
};

const LEGACY_TRADE_LOG_RANGE_KEY = "tradeLog_dateRange";

export const DEFAULT_DASHBOARD_DATE_WINDOW: DashboardDateWindowV1 = {
  v: 1,
  mode: "preset",
  timeframe: "this-month",
  startDate: null,
  endDate: null,
};

/** Call before Clerk sign-out so the next session starts on the default preset. */
export function resetDashboardDateWindowForAuthSession(): void {
  writeDashboardDateWindow(DEFAULT_DASHBOARD_DATE_WINDOW);
}

function normalizePersisted(raw: unknown): DashboardDateWindowV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1) return null;
  if (o.mode !== "preset" && o.mode !== "custom") return null;
  if (typeof o.timeframe !== "string") return null;
  const savedAtMs =
    typeof o.savedAtMs === "number" && Number.isFinite(o.savedAtMs) ? o.savedAtMs : undefined;
  return {
    v: 1,
    mode: o.mode,
    timeframe: o.timeframe,
    startDate: typeof o.startDate === "string" ? o.startDate : null,
    endDate: typeof o.endDate === "string" ? o.endDate : null,
    ...(savedAtMs !== undefined ? { savedAtMs } : {}),
  };
}

/** YYYY-MM-DD in local time — for comparing “which calendar day” without TZ drift. */
function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${m < 10 ? "0" : ""}${m}-${day < 10 ? "0" : ""}${day}`;
}

/**
 * Custom absolute ranges should not live forever in localStorage (beforeunload is not reliable:
 * bfcache, crashes, restored tabs). Invalidate when the save day ≠ today, or (legacy payloads
 * without savedAtMs) when the range ends before the start of today local.
 */
function isStaleCustomDashboardDateWindow(p: DashboardDateWindowV1): boolean {
  if (p.mode !== "custom") return false;

  if (typeof p.savedAtMs === "number" && Number.isFinite(p.savedAtMs)) {
    return localDayKey(new Date(p.savedAtMs)) !== localDayKey(new Date());
  }

  if (!p.endDate) return false;
  const end = new Date(p.endDate);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const endDayStart = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
  return endDayStart < startOfToday;
}

export function writeDashboardDateWindow(p: DashboardDateWindowV1): void {
  try {
    const { savedAtMs: _drop, ...base } = p as DashboardDateWindowV1 & { savedAtMs?: number };
    const payload: DashboardDateWindowV1 =
      p.mode === "custom" ? { ...base, savedAtMs: Date.now() } : base;
    localStorage.setItem(DASHBOARD_DATE_WINDOW_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent(DASHBOARD_DATE_WINDOW_EVENT));
  } catch {
    // no-op
  }
}

function sanitizeDashboardDateWindow(p: DashboardDateWindowV1): DashboardDateWindowV1 {
  if (!isStaleCustomDashboardDateWindow(p)) return p;
  writeDashboardDateWindow(DEFAULT_DASHBOARD_DATE_WINDOW);
  return DEFAULT_DASHBOARD_DATE_WINDOW;
}

function parseStoredDashboardDateWindow(): DashboardDateWindowV1 | null {
  try {
    const raw = localStorage.getItem(DASHBOARD_DATE_WINDOW_KEY);
    if (!raw) return null;
    const parsed = normalizePersisted(JSON.parse(raw));
    if (!parsed) return null;
    return sanitizeDashboardDateWindow(parsed);
  } catch {
    return null;
  }
}

export function readDashboardDateWindow(): DashboardDateWindowV1 | null {
  return parseStoredDashboardDateWindow();
}

export function equalDateRange(a: DateRange | undefined, b: DateRange | undefined): boolean {
  const s1 = a?.startDate?.getTime() ?? null;
  const e1 = a?.endDate?.getTime() ?? null;
  const s2 = b?.startDate?.getTime() ?? null;
  const e2 = b?.endDate?.getTime() ?? null;
  return s1 === s2 && e1 === e2;
}

/** Presets anchored to “today” in local time (must match `presetToLocalDateRange` / DateRangePicker). */
const DASHBOARD_PRESET_TIMEFRAMES = [
  "today",
  "this-week",
  "last-week",
  "this-month",
  "last-month",
  "7-days",
  "14-days",
  "28-days",
  "30-days",
] as const;

/**
 * Order for inferring a preset from dates. Month presets come before week presets so early
 * in the month (when the 1st is a Monday) “This month” wins over “This week”.
 */
const DASHBOARD_PRESET_MATCH_ORDER = [
  "today",
  "this-month",
  "last-month",
  "this-week",
  "last-week",
  "7-days",
  "14-days",
  "28-days",
  "30-days",
] as const;

export type DashboardDatePresetTimeframe =
  (typeof DASHBOARD_PRESET_TIMEFRAMES)[number] | "all-time";

export type DashboardDateRangeChangeOptions = {
  presetId?: DashboardDatePresetTimeframe;
};

export function isDashboardDatePresetTimeframe(
  tf: string
): tf is DashboardDatePresetTimeframe {
  return DASHBOARD_DATE_PRESET_OPTIONS.some((option) => option.id === tf);
}

export function preferredPresetFromPersisted(
  p: DashboardDateWindowV1
): DashboardDatePresetTimeframe | null {
  if (p.mode !== "preset") return null;
  return isDashboardDatePresetTimeframe(p.timeframe) ? p.timeframe : null;
}

/** Header / shadcn date picker quick-select rows (order matters for UX). */
export const DASHBOARD_DATE_PRESET_OPTIONS: ReadonlyArray<{
  id: DashboardDatePresetTimeframe;
  label: string;
}> = [
  { id: "today", label: "Today" },
  { id: "this-week", label: "This week" },
  { id: "last-week", label: "Last week" },
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "7-days", label: "Last 7 days" },
  { id: "28-days", label: "Last 28 days" },
  { id: "30-days", label: "Last 30 days" },
  { id: "14-days", label: "Last 14 days" },
  { id: "all-time", label: "All time" },
] as const;

/** Mobile date picker — five high-use presets; full list on sm+. */
export const DASHBOARD_DATE_PRESET_MOBILE_IDS: readonly DashboardDatePresetTimeframe[] = [
  "today",
  "this-week",
  "this-month",
  "7-days",
  "all-time",
] as const;

export function dashboardDatePresetOptionsForViewport(
  isMobile: boolean,
  activePresetId: DashboardDatePresetTimeframe | null = null
): ReadonlyArray<{ id: DashboardDatePresetTimeframe; label: string }> {
  if (!isMobile) {
    return DASHBOARD_DATE_PRESET_OPTIONS;
  }

  const mobileIds = new Set(DASHBOARD_DATE_PRESET_MOBILE_IDS);
  const mobileOptions = DASHBOARD_DATE_PRESET_OPTIONS.filter((option) =>
    mobileIds.has(option.id)
  );

  if (activePresetId && !mobileIds.has(activePresetId)) {
    const activeOption = DASHBOARD_DATE_PRESET_OPTIONS.find(
      (option) => option.id === activePresetId
    );
    if (activeOption) {
      return [...mobileOptions, activeOption];
    }
  }

  return mobileOptions;
}

/** @deprecated Use `lastWeekMonSunLocalRange` — kept for imports that expected the old name. */
export function lastWeekSunSatLocalRange(): { startDate: Date; endDate: Date } {
  return lastWeekMonSunLocalRange();
}

/** Local calendar range for Trade Log / DateRangePicker-style boundaries. */
export function presetToLocalDateRange(timeframe: string): { startDate: Date; endDate: Date } | null {
  const t = new Date();
  const startOfToday = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0);
  const endOfToday = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
  switch (timeframe) {
    case "today":
      return { startDate: new Date(startOfToday), endDate: new Date(endOfToday) };
    case "this-week":
      return thisWeekMonTodayLocalRange();
    case "last-week":
      return lastWeekMonSunLocalRange();
    case "this-month":
      return thisMonthStartThroughTodayLocalRange();
    case "last-month":
      return lastMonthLocalRange();
    case "7-days": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 7);
      return { startDate: s, endDate: new Date(endOfToday) };
    }
    case "14-days": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 14);
      return { startDate: s, endDate: new Date(endOfToday) };
    }
    case "28-days": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 28);
      return { startDate: s, endDate: new Date(endOfToday) };
    }
    case "30-days": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 30);
      return { startDate: s, endDate: new Date(endOfToday) };
    }
    case "all-time":
      return null;
    default:
      return null;
  }
}

function localDayEqual(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function getActiveDashboardDatePresetId(
  dr: DateRange | undefined,
  preferredPresetId?: DashboardDatePresetTimeframe | string | null
): DashboardDatePresetTimeframe | null {
  if (
    preferredPresetId &&
    isDashboardDatePresetTimeframe(preferredPresetId) &&
    localDateRangeMatchesPreset(dr, preferredPresetId)
  ) {
    return preferredPresetId;
  }
  for (const tf of DASHBOARD_PRESET_MATCH_ORDER) {
    if (localDateRangeMatchesPreset(dr, tf)) return tf;
  }
  if (localDateRangeMatchesPreset(dr, "all-time")) return "all-time";
  return null;
}

export function localDateRangeMatchesPreset(dr: DateRange | undefined, tf: string): boolean {
  if (tf === "all-time") return !dr?.startDate && !dr?.endDate;
  const range = dr ?? { startDate: null, endDate: null };
  if (tf === "this-week") return localDateRangeMatchesThisWeek(range);
  if (tf === "last-week") return localDateRangeMatchesLastWeek(range);
  if (tf === "this-month") return localDateRangeMatchesThisMonth(range);
  if (tf === "last-month") return localDateRangeMatchesLastMonth(range);
  const c = presetToLocalDateRange(tf);
  if (!c || !dr?.startDate || !dr?.endDate) return false;
  return localDayEqual(dr.startDate, c.startDate) && localDayEqual(dr.endDate, c.endDate);
}

/**
 * Maps a concrete local date range to API preset labels.
 * Uses calendar alignment with anchored presets — not raw ms / 24h (which breaks
 * “7 days ago 00:00 → today 23:59” and incorrectly yields the next bucket).
 */
export function deriveTimeframeKeyFromLocalDateRange(dr: DateRange | undefined): string {
  return getActiveDashboardDatePresetId(dr) ?? "all-time";
}

export function persistStateFromTradeLog(
  dateRange: DateRange | undefined,
  derivedTimeframe: string,
  preferredPresetId?: DashboardDatePresetTimeframe | string | null
): DashboardDateWindowV1 {
  if (!dateRange?.startDate && !dateRange?.endDate) {
    return {
      v: 1,
      mode: "preset",
      timeframe: "all-time",
      startDate: null,
      endDate: null,
    };
  }
  if (dateRange.startDate && dateRange.endDate) {
    const activePreset = getActiveDashboardDatePresetId(dateRange, preferredPresetId);
    if (activePreset && activePreset !== "all-time") {
      return {
        v: 1,
        mode: "preset",
        timeframe: activePreset,
        startDate: null,
        endDate: null,
      };
    }
    return {
      v: 1,
      mode: "custom",
      timeframe: derivedTimeframe,
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
    };
  }
  return {
    v: 1,
    mode: "custom",
    timeframe: derivedTimeframe,
    startDate: dateRange.startDate?.toISOString() ?? null,
    endDate: dateRange.endDate?.toISOString() ?? null,
  };
}

export function dateRangeForTradeLogFromPersisted(p: DashboardDateWindowV1): DateRange {
  if (p.mode === "preset") {
    if (p.timeframe === "all-time") return { startDate: null, endDate: null };
    const r = presetToLocalDateRange(p.timeframe);
    return r ?? { startDate: null, endDate: null };
  }
  return {
    startDate: p.startDate ? new Date(p.startDate) : null,
    endDate: p.endDate ? new Date(p.endDate) : null,
  };
}

export function buildPersistedFromStatistics(dateRange: DateRange, timeframe: string): DashboardDateWindowV1 {
  if (dateRange?.startDate || dateRange?.endDate) {
    return {
      v: 1,
      mode: "custom",
      timeframe,
      startDate: dateRange.startDate?.toISOString() ?? null,
      endDate: dateRange.endDate?.toISOString() ?? null,
    };
  }
  return {
    v: 1,
    mode: "preset",
    timeframe: timeframe || "this-month",
    startDate: null,
    endDate: null,
  };
}

export function statisticsStateFromPersisted(p: DashboardDateWindowV1): { dateRange: DateRange; timeframe: string } {
  if (p.mode === "custom") {
    return {
      dateRange: {
        startDate: p.startDate ? new Date(p.startDate) : null,
        endDate: p.endDate ? new Date(p.endDate) : null,
      },
      timeframe: p.timeframe || "this-month",
    };
  }
  // Statistics API has no week presets — use explicit local dates (same as Trade Log picker).
  if (
    p.timeframe === "this-week" ||
    p.timeframe === "last-week" ||
    p.timeframe === "this-month" ||
    p.timeframe === "last-month"
  ) {
    const r = presetToLocalDateRange(p.timeframe);
    if (r) {
      return {
        dateRange: { startDate: r.startDate, endDate: r.endDate },
        timeframe: "today",
      };
    }
  }
  return {
    dateRange: { startDate: null, endDate: null },
    timeframe: p.timeframe || "this-month",
  };
}

export function buildPersistedFromReports(
  useCustomRange: boolean,
  timeframe: string,
  dateRange: DateRange
): DashboardDateWindowV1 {
  // Persist custom mode even when dates are not chosen yet — otherwise a write + listener
  // round-trip immediately flips UI back to preset (Execution rules / other views).
  if (useCustomRange) {
    return {
      v: 1,
      mode: "custom",
      timeframe,
      startDate: dateRange.startDate?.toISOString() ?? null,
      endDate: dateRange.endDate?.toISOString() ?? null,
    };
  }
  return {
    v: 1,
    mode: "preset",
    timeframe: timeframe || "this-month",
    startDate: null,
    endDate: null,
  };
}

export function reportsStateFromPersisted(p: DashboardDateWindowV1): {
  useCustomRange: boolean;
  timeframe: string;
  dateRange: DateRange;
} {
  if (p.mode === "custom") {
    return {
      useCustomRange: true,
      timeframe: p.timeframe || "this-month",
      dateRange: {
        startDate: p.startDate ? new Date(p.startDate) : null,
        endDate: p.endDate ? new Date(p.endDate) : null,
      },
    };
  }
  if (
    p.timeframe === "this-week" ||
    p.timeframe === "last-week" ||
    p.timeframe === "this-month" ||
    p.timeframe === "last-month"
  ) {
    const r = presetToLocalDateRange(p.timeframe);
    if (r) {
      return {
        useCustomRange: true,
        timeframe: "today",
        dateRange: { startDate: r.startDate, endDate: r.endDate },
      };
    }
  }
  return {
    useCustomRange: false,
    timeframe: p.timeframe || "this-month",
    dateRange: { startDate: null, endDate: null },
  };
}

export function dashboardTimeframeFromPersisted(p: DashboardDateWindowV1): string {
  if (p.mode === "preset") return p.timeframe || "this-month";
  const dr: DateRange = {
    startDate: p.startDate ? new Date(p.startDate) : null,
    endDate: p.endDate ? new Date(p.endDate) : null,
  };
  return deriveTimeframeKeyFromLocalDateRange(dr);
}

/**
 * Use for initial React state. Migrates legacy `tradeLog_dateRange` into the v1 key once.
 */
export function getInitialDashboardDateWindow(): DashboardDateWindowV1 {
  try {
    const fromKey = parseStoredDashboardDateWindow();
    if (fromKey) return fromKey;

    const legacy = localStorage.getItem(LEGACY_TRADE_LOG_RANGE_KEY);
    if (legacy) {
      const dr = JSON.parse(legacy) as { startDate?: string; endDate?: string };
      const startDate = dr.startDate ? new Date(dr.startDate) : null;
      const endDate = dr.endDate ? new Date(dr.endDate) : null;
      const tf = deriveTimeframeKeyFromLocalDateRange({ startDate, endDate });
      const migrated = persistStateFromTradeLog({ startDate, endDate }, tf);
      if (isStaleCustomDashboardDateWindow(migrated)) {
        writeDashboardDateWindow(DEFAULT_DASHBOARD_DATE_WINDOW);
        return DEFAULT_DASHBOARD_DATE_WINDOW;
      }
      writeDashboardDateWindow(migrated);
      return readDashboardDateWindow() ?? DEFAULT_DASHBOARD_DATE_WINDOW;
    }
  } catch {
    // fall through
  }
  return DEFAULT_DASHBOARD_DATE_WINDOW;
}

/** Lowercase period label for chart subtitles (e.g. `this week`, `last 7 days`). */
export function dashboardPresetChartPeriodLabel(timeframe: string): string {
  switch (timeframe) {
    case "all-time":
      return "since all time";
    case "today":
      return "today";
    case "this-week":
      return "this week";
    case "last-week":
      return "last week";
    case "this-month":
      return "this month";
    case "last-month":
      return "last month";
    case "7-days":
    case "14-days":
    case "28-days":
    case "30-days": {
      const m = /^(\d+)-days$/.exec(timeframe);
      return m ? `last ${m[1]} days` : timeframe;
    }
    default: {
      const m = /^(\d+)-days$/.exec(timeframe);
      if (m) return `last ${m[1]} days`;
      return timeframe.replace(/-/g, " ");
    }
  }
}

/** Caption beside mini-stat % change for the comparison baseline. */
export function miniStatComparisonCaptionForTimeframe(timeframe: string): string {
  switch (timeframe) {
    case "all-time":
      return "since all time";
    case "today":
      return "vs yesterday";
    case "this-week":
      return "vs last week";
    case "last-week":
      return "vs prior week";
    case "this-month":
      return "vs last month";
    case "last-month":
      return "vs prior month";
    case "7-days":
      return "vs past 7 days";
    case "14-days":
      return "vs past 14 days";
    case "28-days":
      return "vs past 28 days";
    case "30-days":
      return "vs past 30 days";
    default: {
      const m = /^(\d+)-days$/.exec(timeframe);
      if (m) return `vs past ${m[1]} days`;
      return "vs prior period";
    }
  }
}
